import React, {
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import styles from "./superAdminAccounts.module.scss";
import Button from "../../../components/button/Button";
import Spinner from "../../../components/spinner/Spinner";
import {
  BodyColumn,
  HeaderColumn,
  SortingState,
} from "../../../components/table/Table";
import AddModal from "./add-modal/AddModal";
import { useUser } from "../../../hooks/useUser";
import { useClientPagination } from "../../../hooks/useClientPagination";
import { formatDate } from "@utils/time";
import {
  useCustomersQuery,
  ExtendedCustomerFragment as Account,
  useDeleteAccountMutation,
} from "@api/graphql-codegen";
import IconWarning from "@src/assets/icons/modal-warning.svg?react";
import { countryOptions, IconInfo } from "@constants";
import { SelectOption, AccountStatus } from "@types";
import { useToast } from "@src/hooks/useToast";
import NormalModal from "@src/components/modal/normal-modal/NormalModal";
import { LordIcon } from "@src/components/lord-icon/lord-icon";
import { TextInput } from "@src/components/text-input/TextInput";
import {
  AdminGrid,
  AdminTable,
  CountryFlag,
  ActionButtons,
} from "@src/components/admin-grid";
import FilterSection from "@src/components/filter-section";
import SentryTrackingButton from "@src/components/sentry-tracking/SentryTrackingButton";

// Add global style to force icons to be visible on hover and ensure ellipsis works
const GlobalStyle = () => {
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      tr:hover .${styles.iconGroup} {
        display: flex !important;
        opacity: 1 !important;
        visibility: visible !important;
      }
      
      /* Force ellipsis to show up */
      div[class*="superAdminAccounts_cellContent"] {
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
        max-width: calc(100% - 50px) !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  return null;
};

type Props = {};

const statusOptions = [
  { value: AccountStatus.Active, label: AccountStatus.Active },
  { value: AccountStatus.Expired, label: AccountStatus.Expired },
];

const trialFilterOptions = [
  { value: "Trial", label: "Trial" },
  { value: "Customer", label: "Customer" },
];

function filterCustomers(search: string) {
  const s = search.toLowerCase();
  return (c: Account) => {
    if (c.name.toLowerCase().includes(s)) return true;
    return false;
  };
}

function SuperAdminAccounts(props: Props) {
  const history = useHistory();
  const [me] = useUser();

  const [deleteAccount, { loading: deleting }] = useDeleteAccountMutation();

  const [showAddModal, setShowAddModal] = useState(false);
  const [customer, setCustomer] = useState<Account>();

  const [perPage] = useState(50);

  const [countryFilter, setCountryFilter] = useState("");
  const [trialState, setTrialState] = useState<string | undefined>();
  const [status, setStatus] = useState("");

  const [search, setSearch] = useState("");
  const [tableSorting, setTableSorting] = useState<SortingState | undefined>();
  const [isReset, setIsReset] = useState(false);

  const [showDeletModal, setShowDeleteModal] = useState(false);
  const [confirmDeletModal, setConfirmDeleteModal] = useState(false);
  const [deleteText, setDeletText] = useState("");

  const { successToast, errorToast } = useToast();

  const { data, loading, refetch } = useCustomersQuery({
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      if (data && data.allCustomers && data.allCustomers.length > 0) {
        setIsReset(false);
      }
    },
  });

  const handleResetFilters = useCallback(() => {
    // Reset all filter states
    setCountryFilter("");
    setSearch("");
    setTrialState(undefined);
    setStatus("");
    setTableSorting(undefined);

    // Set the reset flag to true to show empty results
    setIsReset(true);
  }, []);

  const filter = useCallback(
    (arr) => {
      const now = new Date().valueOf();
      let accounts = [...(arr || []).filter(filterCustomers(search))];

      if (trialState) {
        accounts = accounts.filter((d) =>
          trialState === "Trial" ? !!d.trial : !d.trial,
        );
      }

      if (countryFilter) {
        accounts = accounts.filter((d) => d.id.startsWith(countryFilter));
      }

      if (status) {
        accounts = accounts.filter((d) =>
          d.expiry && Number(d.expiry) < now
            ? status === AccountStatus.Expired
            : status === AccountStatus.Active,
        );
      }

      return accounts;
    },
    [search, trialState, countryFilter, status],
  );

  const paginationSort = useMemo(
    () =>
      tableSorting
        ? {
            key: tableSorting.key,
            dir: tableSorting.dir,
            compare: tableSorting.func,
          }
        : undefined,
    [tableSorting],
  );

  const { dataSlice, totalFilteredItems, currentPage, onChangePage } =
    useClientPagination({
      // If isReset is true, use an empty array instead of the actual data
      data: isReset ? [] : data?.allCustomers || undefined,
      perPage,
      onFilter: filter,
      sort: paginationSort,
    });

  const handleOpenModal = () => setShowAddModal(true);

  const handleCloseModal = () => {
    setCustomer(undefined);
    setShowAddModal(false);
    setShowDeleteModal(false);
    setConfirmDeleteModal(false);
  };

  const handleEditCustomer = useCallback(
    (c: Account) => () => {
      setCustomer(c);
      handleOpenModal();
    },
    [],
  );

  const handleDelCustomer = useCallback(
    (c: Account) => () => {
      setCustomer(c);
      setShowDeleteModal(true);
    },
    [],
  );

  const handleConfirmRemove = useCallback(() => {
    setShowDeleteModal(false);
    setConfirmDeleteModal(true);
    setDeletText("");
  }, []);

  const handleRemoveAccount = useCallback(async () => {
    if (!customer?.id) {
      setConfirmDeleteModal(false);
      errorToast("No account selected. Please try again.");
      return;
    }

    try {
      const response = await deleteAccount({
        variables: {
          input: {
            id: customer.id,
          },
        },
      });

      if (response.data?.deleteAccount) {
        successToast("Account successfully deleted!");
        setConfirmDeleteModal(false);
        refetch();
      } else {
        setConfirmDeleteModal(false);
        errorToast("Failed to delete account. Please try again.");
      }
    } catch {
      setConfirmDeleteModal(false);
      errorToast("Failed to delete account. Please try again.");
    }
  }, [customer?.id, deleteAccount, errorToast, refetch, successToast]);

  const handleEditSchools = useCallback(
    (row: Account) => () => {
      history.push(`${history.location.pathname}/${row.id}/schools`, {
        customer: row,
      });
    },
    [history],
  );

  const handleCopyToClipboard = useCallback(
    (text: string) => {
      successToast(text);
    },
    [successToast],
  );

  const handleRefetch = useCallback(() => refetch(), [refetch]);

  const headerColumns: HeaderColumn[] = useMemo(() => {
    return [
      {
        title: "Name",
        width: "30%",
        minWidth: 260,
        maxWidth: 400,
        sort: {
          key: "name",
          asc: (a: Account, b: Account) => a.name.localeCompare(b.name) || 0,
          desc: (a: Account, b: Account) => b.name.localeCompare(a.name) || 0,
        },
      },
      {
        title: "Country",
        center: true,
        width: 60,
        sort: {
          key: "country",
          asc: (a: Account, b: Account) =>
            a?.country?.localeCompare(b?.country || "") || 0,
          desc: (a: Account, b: Account) =>
            b?.country?.localeCompare(a?.country || "") || 0,
        },
      },
      {
        title: "End Date",
        width: 80,
        sort: {
          key: "expiry",
          asc: (a: Account, b: Account) =>
            a?.expiry?.localeCompare(b?.expiry || "") || 0,
          desc: (a: Account, b: Account) =>
            b?.expiry?.localeCompare(a?.expiry || "") || 0,
        },
      },
      { title: "Status", width: 60 },
      { title: "Trial", width: 60 },
      { title: "Actions", right: true, width: 200 },
    ];
  }, []);

  const bodyColumns: BodyColumn[] = useMemo(() => {
    const now = new Date().valueOf();
    return [
      {
        key: (row: Account, column, options) => (
          <div className={styles.cellWithCopyIcon}>
            <div className={styles.cellContent} title={row.name}>
              <div
                style={{
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                {row.name}
              </div>
            </div>
            <div
              className={styles.iconGroup}
              style={{ position: "absolute", right: "0px" }}
            >
              <ActionButtons
                className={styles.nameActionsContainer}
                tooltipPosition="top"
                showEdit={false}
                showDelete={false}
                showView={false}
                onCopy={handleCopyToClipboard}
                copyText={row.id}
                copyTooltip={`Copy account ID: ${row.id}`}
                isRowHovered={options?.isRowHovered}
              />
            </div>
          </div>
        ),
      },
      {
        key: (row: Account, column, options) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <CountryFlag countryCode={row.id.substring(0, 2)} />
          </div>
        ),
      },
      {
        key: (row: Account, column, options) => (
          <div className={styles.tableTitle}>
            {row.expiry ? formatDate(row.expiry) : ""}
          </div>
        ),
      },
      {
        key: (row: Account, column, options) => (
          <div>
            {row.expiry && Number(row.expiry) < now ? (
              <span className={styles.expiredBadge}>Expired</span>
            ) : (
              <span className={styles.trialBadge}>Active</span>
            )}
          </div>
        ),
      },
      {
        key: (row: Account, column, options) => (
          <div>
            {row.trial ? (
              <span className={styles.trialBadge}>Trial</span>
            ) : (
              <div></div>
            )}
            {row.isDemo ? (
              <span className={styles.trialBadge}>Demo</span>
            ) : null}
          </div>
        ),
      },
      {
        key: (row: Account, column, options) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <ActionButtons
              className={styles.actionsContainer}
              tooltipPosition="top"
              onEdit={handleEditCustomer(row)}
              editTooltip="Edit account"
              onDelete={handleDelCustomer(row)}
              deleteTooltip="Delete account"
              onCopy={handleCopyToClipboard}
              copyText={`${window.location.protocol + "//" + window.location.host}/registration?id=${row.id}&entity=acc`}
              copyTooltip="Copy registration link for the account"
              onView={handleEditSchools(row)}
              viewLabel="View Schools"
            />
            <SentryTrackingButton
              entityId={row.id}
              entityType="account"
              entityName={row.name}
              buttonText="Track"
              buttonSize="small"
              onTrackingAdded={handleRefetch}
              isRowHovered={options?.isRowHovered}
            />
          </div>
        ),
      },
    ];
  }, [
    handleCopyToClipboard,
    handleEditCustomer,
    handleDelCustomer,
    handleEditSchools,
    handleRefetch,
  ]);

  const handleChangeSearch = (value: string) => setSearch(value);
  const handleChangeDelText = (value: any) => setDeletText(value);

  const handleOpenAddNew = () => {
    handleOpenModal();
  };

  const hasData = !loading && data?.allCustomers?.length !== 0;

  const handleChangePage = (page: number) => {
    onChangePage(page);
  };

  const handleChangeFilter = useCallback(
    (setter: Dispatch<string>) => (o: SelectOption) => {
      setter(o.value);
    },
    [],
  );

  // Filter section component using the reusable FilterSection component
  const filterSection = (
    <FilterSection
      searchValue={search}
      onSearchChange={handleChangeSearch}
      filters={[
        {
          value: countryFilter,
          onChange: handleChangeFilter(setCountryFilter),
          label: "Country",
          options: countryOptions,
          showSearch: false,
        },
        {
          value: status,
          onChange: handleChangeFilter(setStatus),
          label: "Status",
          options: statusOptions,
        },
        {
          value: trialState || "",
          onChange: handleChangeFilter(setTrialState),
          label: "Is Trial",
          options: trialFilterOptions,
        },
      ]}
      actionButton={{
        onClick: handleRefetch,
        className: styles.searchBtn,
        label: "Search",
        color: "orange",
        size: "normal",
      }}
      onReset={handleResetFilters}
    />
  );

  // Header actions component
  const headerActions = hasData && (
    <Button
      onClick={handleOpenAddNew}
      className={styles.inviteBtn}
      label={"ADD NEW"}
      size={"small"}
    />
  );

  return (
    <>
      <GlobalStyle />
      <AdminGrid
        title="Accounts"
        filterSection={filterSection}
        headerActions={headerActions}
        resultsCount={totalFilteredItems}
        resultsCountLabel="Accounts"
      >
        {hasData || loading ? (
          loading ? (
            <div className={styles.spinnerContainer}>
              <Spinner />
            </div>
          ) : (
            <AdminTable
              header={headerColumns}
              body={bodyColumns}
              data={dataSlice}
              loading={loading}
              currentPage={currentPage}
              perPage={perPage}
              totalItems={totalFilteredItems}
              onPageChange={handleChangePage}
              color="green"
              sorting={tableSorting}
              onSortingChange={setTableSorting}
            />
          )
        ) : (
          <div className={styles.hasNoDataWrapper}>
            <div className={styles.hasNoData}>
              <div className={styles.hasNoDataText}>
                <div>The list is empty for now.</div>
                <div>You can create an account by clicking</div>
              </div>
              <Button
                onClick={handleOpenAddNew}
                className={styles.inviteBtn}
                label={"ADD NEW"}
                size={"small"}
              />
            </div>
          </div>
        )}
      </AdminGrid>
      {showAddModal && (
        <AddModal
          onCompleted={handleRefetch}
          open={showAddModal}
          onClose={handleCloseModal}
          customer={customer}
          me={me}
        />
      )}
      {showDeletModal && (
        <NormalModal
          logoIcon={<IconWarning />}
          title={"WARNING?"}
          open={showDeletModal}
          onClose={handleCloseModal}
          description={
            <div className={styles.detail}>
              This will permanently delete all Schools, Users, and Analytics in{" "}
              <strong>{customer?.name}</strong>. This cannot be undone.
            </div>
          }
          leftButton={
            <Button
              size="small"
              color="green"
              className={styles.cancelButton}
              disabled={false}
              onClick={handleCloseModal}
              label={"CANCEL"}
            />
          }
          rightButton={
            <Button
              size="small"
              color="red"
              className={styles.resetButton}
              disabled={false}
              onClick={handleConfirmRemove}
              label={"DELET ACCOUNT"}
            />
          }
        />
      )}
      {confirmDeletModal && (
        <NormalModal
          logoIcon={<IconWarning className={styles.redWarning} />}
          title={<div className={styles.redWarningTitle}>CONFIRM DELETION</div>}
          open={confirmDeletModal}
          onClose={() => {
            setConfirmDeleteModal(false);
          }}
          loading={deleting}
          description={
            <div className={styles.redDetail}>
              Please type the characters "DELETE" to remove{" "}
              <strong>{customer?.name}</strong>. This cannot be undone.
              <TextInput
                className={styles.deleteInput}
                id="deleteInput"
                label=""
                value={deleteText}
                placeholder='Please type the characters "DELETE"'
                onChange={handleChangeDelText}
              />
            </div>
          }
          leftButton={
            <Button
              size="small"
              color="green"
              className={styles.cancelButton}
              disabled={deleting}
              onClick={() => {
                setConfirmDeleteModal(false);
              }}
              label={"CANCEL"}
            />
          }
          rightButton={
            <Button
              size="small"
              color="red"
              className={styles.resetButton}
              disabled={deleting || deleteText !== "DELETE"}
              isLoading={deleting}
              onClick={handleRemoveAccount}
              label={"DELETE ACCOUNT"}
              rightIcon={
                <div className={styles.infoIcon}>
                  <LordIcon
                    src={IconInfo}
                    isHover={true}
                    colors={{ primary: "#FFFFFF", secondary: "#FFFFFF" }}
                    size={23.4}
                  />
                </div>
              }
            />
          }
        />
      )}
    </>
  );
}

export default SuperAdminAccounts;
